/**
 * Where login should put the user down.
 *
 * The "your translated document is ready" email links straight at a page — and an email click is
 * very often a cold click, with no session in that browser. The requested page is therefore
 * reached *through* the IdP, so the destination has to survive that round trip or the user lands
 * on the upload form instead, being asked to upload something they were told was finished.
 *
 * It survives as OIDC `url_state`: set on the signin request, handed back on the `User` once the
 * callback resolves. These two helpers are the ends of that trip, kept pure so the rules are
 * readable on their own. COPA-505.
 */

/** Where the app lands when nothing else was asked for — the same page `App.tsx`'s catch-all uses. */
export const DEFAULT_POST_LOGIN_PATH = '/upload';

/**
 * Paths not worth carrying through login: the default is where we would land anyway, and the auth
 * routes would either loop the user back through login or drop them on a bare spinner.
 */
const NOT_WORTH_RETURNING_TO = new Set([DEFAULT_POST_LOGIN_PATH, '/', '/auth/callback', '/login']);

/**
 * Roomy for any route we have, small enough that nothing bulky rides along in the state parameter.
 * Enforced in both directions: an oversized value sent out is an authorization request some IdPs
 * reject outright, which would break the very login this exists to get the user through.
 */
const MAX_LENGTH = 512;

/** The address a visitor was refused, in the form `resolveReturnTo` expects back. */
export function captureReturnTo({
  pathname,
  search,
  hash,
}: {
  pathname: string;
  search: string;
  hash: string;
}): string | undefined {
  if (NOT_WORTH_RETURNING_TO.has(pathname)) return undefined;
  const address = `${pathname}${search}${hash}`;
  return address.length > MAX_LENGTH ? undefined : address;
}

/**
 * The address to navigate to after a successful callback, or `undefined` for "nowhere in
 * particular" — the caller decides the default.
 *
 * The value has been outside the app since we set it, so it is treated as input rather than as
 * something we wrote: only an in-app path is honoured, and anything that could name another
 * origin is dropped.
 */
export function resolveReturnTo(urlState: unknown): string | undefined {
  if (typeof urlState !== 'string' || urlState.length > MAX_LENGTH) return undefined;
  // `//host` and `/\host` are both read as an authority, not as a path on this origin.
  if (!urlState.startsWith('/') || urlState.startsWith('//') || urlState.startsWith('/\\')) return undefined;
  const [pathname] = urlState.split(/[?#]/, 1);
  if (NOT_WORTH_RETURNING_TO.has(pathname)) return undefined;
  return urlState;
}
